import {ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit, signal, Signal, untracked} from '@angular/core';
import {Store} from '@ngrx/store';
import {IHasUnsavedChanges, UnsavedChangesService} from '../../services/unsaved-changes/unsaved-changes.service';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {
  IMultiplePriceChangeResult,
  IMultiplePriceChangeRule,
  IMultiplePriceChangeRulesStoreSettings,
  IMultiplePriceGpAction,
  MULTIPLE_PRICE_CHANGE_PRICE_INDEXES,
  SettingsReferenceEnum,
  TMultiplePriceConditionOperator,
  TMultiplePriceConditionSource,
  TMultiplePriceGpMode,
} from '../../models';
import {
  selectIsMultiplePriceChangeRulesLoading,
  selectIsMultiplePriceChangeRulesSaving,
  selectSelectedSetting,
  selectSelectedStoreMultiplePriceChangeRules,
  selectSelectedStorePriceBands,
} from '../../store/setting.selectors';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {ICrudOptions, IUserAccessFeatureStockRules} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureStockRulesForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {settingsSetSelected, settingStoreMultiplePriceChangeRules} from '../../store/setting.actions';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {getDeepCopyOfObject, isNonEmptyObject} from '../../../../shared/shared-utils/object/object.utils';
import {VatRates} from '../../../../shared-utilities/models-old/datastructures';
import {selectSharedGridVatRateBySelectedStore} from '../../../../shared-modules/shared-grid/store/shared-grid.selectors';
import {
  calculateMultiplePriceChanges,
  createDefaultMultiplePriceChangeRule,
  createDefaultMultiplePriceChangeSettings,
  describeMultiplePriceChangeRule,
  validateMultiplePriceChangeRules,
} from '../../../../shared/shared-utils/price/multiple-price-change.utils';
import {calculateGPValue} from '../../../../shared/shared-utils/calculations/calculations-stock';

const MULTIPLE_PRICE_PREVIEW_FALLBACK_VAT_RATES: VatRates = {[1]: {desc: 'Standard', vatRate: 15}};

type TRuleBranch = 'whenTrue' | 'whenFalse';

type TDisabledRulesChoice = 'cancel' | 'enable' | 'saveDisabled';

@Component({
  selector: 'app-feature-stock-multiple-price-changes',
  templateUrl: './feature-stock-multiple-price-changes.component.html',
  styleUrl: './feature-stock-multiple-price-changes.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureStockMultiplePriceChangesComponent implements OnInit, OnDestroy, IHasUnsavedChanges {

  private readonly formStore = inject(Store);
  private readonly modalUtils = inject(SharedModalUtils);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly settingChanged: Signal<SettingsReferenceEnum> = this.formStore.selectSignal(selectSelectedSetting);
  private readonly storeSettings: Signal<IMultiplePriceChangeRulesStoreSettings> =
    this.formStore.selectSignal(selectSelectedStoreMultiplePriceChangeRules);
  private readonly selectedStoreSignal: Signal<IStore> = this.formStore.selectSignal(selectSelectedStore);
  private readonly vatRates: Signal<VatRates> = this.formStore.selectSignal(selectSharedGridVatRateBySelectedStore);
  private readonly priceBandingView = this.formStore.selectSignal(selectSelectedStorePriceBands);
  private readonly uaStockRules: Signal<IUserAccessFeatureStockRules> = this.formStore.selectSignal(
    selectUserAccessFeatureStockRulesForSelectedStore,
  );

  protected readonly isLoading: Signal<boolean> = this.formStore.selectSignal(selectIsMultiplePriceChangeRulesLoading);
  protected readonly isSaving: Signal<boolean> = this.formStore.selectSignal(selectIsMultiplePriceChangeRulesSaving);

  protected readonly draftSettings = signal<IMultiplePriceChangeRulesStoreSettings>(createDefaultMultiplePriceChangeSettings());
  protected readonly expandedPriceIndex = signal<number | null>(null);
  protected readonly previewCost = signal<string>('50.00');
  protected readonly previewPrice = signal<string>('79.99');
  protected readonly previewTargetGp = signal<string>('25.00');

  private readonly draftStoreId = signal<string>('');
  private readonly hasHydrated = signal<boolean>(false);
  private readonly lastHydratedSnapshot = signal<string>('');
  private readonly pendingSaveSnapshot = signal<string | null>(null);

  protected readonly rules = computed<IMultiplePriceChangeRule[]>(() => this.draftSettings().rules ?? []);

  protected readonly hasDraftEdits = computed<boolean>(() => {
    if (!this.hasHydrated()) {
      return false;
    }
    return this.snapshotOf(this.draftSettings()) !== this.lastHydratedSnapshot();
  });

  protected readonly validationErrors = computed<string[]>(() => validateMultiplePriceChangeRules(this.draftSettings()));

  protected readonly availablePriceIndexes = computed<number[]>(() => {
    const used: number[] = this.rules().map((rule: IMultiplePriceChangeRule) => rule.priceIndex);
    return MULTIPLE_PRICE_CHANGE_PRICE_INDEXES.filter((priceIndex: number) => !used.includes(priceIndex));
  });

  protected readonly previewPriceOneGp = computed<number>(() => {
    const cost: number = Number(this.previewCost());
    const price: number = Number(this.previewPrice());
    if (Number.isNaN(cost) || Number.isNaN(price) || price <= 0) {
      return 0;
    }
    return Number(calculateGPValue(this.previewVatPercentage(), price, cost).toFixed(2));
  });

  protected readonly previewResults = computed<IMultiplePriceChangeResult[]>(() => {
    const cost: number = Number(this.previewCost());
    const price: number = Number(this.previewPrice());
    if (Number.isNaN(cost) || Number.isNaN(price) || price <= 0) {
      return [];
    }
    return calculateMultiplePriceChanges({
      latestCost: cost,
      priceBands: this.priceBandingView()?.bands ?? [],
      sellPriceIncluding1: price,
      settings: this.draftSettings(),
      targetGp: Number(this.previewTargetGp()) || 0,
      vatKey: this.previewVatKey(),
      vatRates: this.previewVatRates(),
    });
  });

  constructor() {
    effect(() => {
      const view: IMultiplePriceChangeRulesStoreSettings = this.storeSettings();
      const storeId: string = this.selectedStoreSignal()?.storeId ?? '';
      const isSaving: boolean = this.isSaving();

      untracked(() => {
        const incomingSnapshot: string = this.snapshotOf(view);
        const draftSnapshot: string = this.snapshotOf(this.draftSettings());
        const pendingSnapshot: string | null = this.pendingSaveSnapshot();

        if (pendingSnapshot !== null && !isSaving) {
          this.pendingSaveSnapshot.set(null);
          if (incomingSnapshot === pendingSnapshot) {
            this.lastHydratedSnapshot.set(draftSnapshot);
            this.hasHydrated.set(true);
            return;
          }
        }
        if (this.draftStoreId() === storeId && this.lastHydratedSnapshot() === incomingSnapshot) {
          return;
        }
        if (this.draftStoreId() === storeId && incomingSnapshot === draftSnapshot) {
          this.lastHydratedSnapshot.set(incomingSnapshot);
          this.hasHydrated.set(true);
          return;
        }
        if (this.draftStoreId() === storeId && this.hasDraftEdits()) {
          return;
        }
        this.hydrateFromView(view, storeId);
      });
    });

    effect(() => {
      const ref: SettingsReferenceEnum = this.settingChanged();
      this.formStore.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });

    this.unsavedChangesService.registerComponent(this);
  }

  get haveAnyEditsBeenMade(): boolean {
    return this.hasDraftEdits();
  }

  get multiplePriceChangesAccess(): ICrudOptions {
    return this.uaStockRules()?.multiplePriceChanges ?? {
      canEdit: true,
      canSubmit: true,
      canView: true,
      isEdited: false,
    };
  }

  get canRulesBeEdited(): boolean {
    return this.multiplePriceChangesAccess?.canEdit ?? true;
  }

  get canRulesBeSubmitted(): boolean {
    return this.multiplePriceChangesAccess?.canSubmit ?? true;
  }

  get canRulesBeViewed(): boolean {
    return this.multiplePriceChangesAccess?.canView ?? true;
  }

  get isSubmitDisabled(): boolean {
    return !this.canRulesBeSubmitted
      || !this.haveAnyEditsBeenMade
      || this.validationErrors().length > 0
      || this.isLoading()
      || this.isSaving();
  }

  ngOnInit(): void {
    this.formStore.dispatch(settingStoreMultiplePriceChangeRules.getMultiplePriceChangeRules({
      params: {storeAction: 'Selected'},
    }));
    this.hydrateFromView(this.storeSettings(), this.selectedStoreSignal()?.storeId ?? '');
  }

  ngOnDestroy(): void {
    this.unsavedChangesService.unregisterComponent();
  }

  /**
   * Collapsed accordion summary for a configured price.
   */
  protected describeRule(rule: IMultiplePriceChangeRule): string {
    return describeMultiplePriceChangeRule(rule);
  }

  protected formatMoney(value: number): string {
    return `R ${Number(value ?? 0).toFixed(2)}`;
  }

  /**
   * Dropdown options for the GP mode of a rule branch.
   */
  protected modeOptions(rule: IMultiplePriceChangeRule, branch: TRuleBranch): Record<string, ISelectOption> {
    const mode: TMultiplePriceGpMode = rule[branch].mode;
    return {
      fixed: {name: 'Fixed GP %', disabled: false, selected: mode === 'fixed'},
      variable: {name: 'Price 1 GP + / - offset', disabled: false, selected: mode === 'variable'},
      none: {name: 'Leave price unchanged', disabled: false, selected: mode === 'none'},
    };
  }

  /**
   * Dropdown options for which GP the condition is measured against.
   */
  protected conditionSourceOptions(rule: IMultiplePriceChangeRule): Record<string, ISelectOption> {
    const source: TMultiplePriceConditionSource = rule.condition.source;
    return {
      priceOneGp: {name: 'Price 1 GP', disabled: false, selected: source !== 'targetGp'},
      targetGp: {name: 'Target GP %', disabled: false, selected: source === 'targetGp'},
    };
  }

  /**
   * Dropdown options for the comparison applied to the chosen GP.
   */
  protected operatorOptions(rule: IMultiplePriceChangeRule): Record<string, ISelectOption> {
    const operator: TMultiplePriceConditionOperator = rule.condition.operator;
    return {
      always: {name: 'Always apply', disabled: false, selected: operator === 'always'},
      greaterThan: {name: 'Is greater than', disabled: false, selected: operator === 'greaterThan'},
      greaterThanOrEqual: {name: 'Is greater than or equal to', disabled: false, selected: operator === 'greaterThanOrEqual'},
      lessThan: {name: 'Is less than', disabled: false, selected: operator === 'lessThan'},
      lessThanOrEqual: {name: 'Is less than or equal to', disabled: false, selected: operator === 'lessThanOrEqual'},
      equalTo: {name: 'Equals', disabled: false, selected: operator === 'equalTo'},
    };
  }

  protected onToggleRuleExpanded(priceIndex: number): void {
    this.expandedPriceIndex.set(this.expandedPriceIndex() === priceIndex ? null : priceIndex);
  }

  protected onToggleFeatureEnabled(enabled: boolean): void {
    if (!this.canRulesBeEdited) {
      return;
    }
    this.draftSettings.set({...this.draftSettings(), enabled});
  }

  protected onToggleRuleEnabled(priceIndex: number, enabled: boolean): void {
    this.patchRule(priceIndex, {enabled});
  }

  /**
   * Adds the next unconfigured price (2-10) to the draft.
   */
  protected onAddPriceRule(): void {
    if (!this.canRulesBeEdited) {
      return;
    }
    const nextIndex: number | undefined = this.availablePriceIndexes()[0];
    if (nextIndex === undefined) {
      return;
    }
    const rules: IMultiplePriceChangeRule[] = [...this.rules(), createDefaultMultiplePriceChangeRule(nextIndex)]
      .sort((left: IMultiplePriceChangeRule, right: IMultiplePriceChangeRule) => left.priceIndex - right.priceIndex);
    this.draftSettings.set({...this.draftSettings(), rules});
    this.expandedPriceIndex.set(nextIndex);
  }

  protected async onRemovePriceRule(priceIndex: number): Promise<void> {
    if (!this.canRulesBeEdited) {
      return;
    }
    const confirmed: boolean = await this.confirmAction(
      `Remove the rule for price ${priceIndex}?`,
      ['This price will no longer change when retail price 1 is edited. Save to apply the change.'],
      'Remove Rule',
    );
    if (!confirmed) {
      return;
    }
    const rules: IMultiplePriceChangeRule[] = this.rules().filter((rule: IMultiplePriceChangeRule) => rule.priceIndex !== priceIndex);
    this.draftSettings.set({...this.draftSettings(), rules});
    this.expandedPriceIndex.set(null);
  }

  protected async onClearAllRules(): Promise<void> {
    if (!this.canRulesBeEdited || this.rules().length === 0) {
      return;
    }
    const confirmed: boolean = await this.confirmAction(
      'Clear all price rules?',
      [
        'Every rule for prices 2-10 will be removed from the draft.',
        'Save to apply the change, or undo to bring them back.',
      ],
      'Clear All Rules',
    );
    if (!confirmed) {
      return;
    }
    this.draftSettings.set({...this.draftSettings(), rules: []});
    this.expandedPriceIndex.set(null);
  }

  protected onConditionSourceChange(priceIndex: number, selected: Record<string, ISelectOption>): void {
    const key: string | undefined = Object.keys(selected).find((option: string) => selected[option].selected);
    if (!key) {
      return;
    }
    const rule: IMultiplePriceChangeRule | undefined = this.findRule(priceIndex);
    if (!rule) {
      return;
    }
    this.patchRule(priceIndex, {
      condition: {...rule.condition, source: key as TMultiplePriceConditionSource},
    });
  }

  protected onConditionOperatorChange(priceIndex: number, selected: Record<string, ISelectOption>): void {
    const key: string | undefined = Object.keys(selected).find((option: string) => selected[option].selected);
    if (!key) {
      return;
    }
    const rule: IMultiplePriceChangeRule | undefined = this.findRule(priceIndex);
    if (!rule) {
      return;
    }
    const operator: TMultiplePriceConditionOperator = key as TMultiplePriceConditionOperator;
    this.patchRule(priceIndex, {
      condition: {...rule.condition, operator},
    });
  }

  protected onConditionValueChange(priceIndex: number, event: Event): void {
    const value: number = Number(this.readInputValue(event));
    if (Number.isNaN(value)) {
      return;
    }
    const rule: IMultiplePriceChangeRule | undefined = this.findRule(priceIndex);
    if (!rule) {
      return;
    }
    this.patchRule(priceIndex, {condition: {...rule.condition, value}});
  }

  protected onBranchModeChange(priceIndex: number, branch: TRuleBranch, selected: Record<string, ISelectOption>): void {
    const key: string | undefined = Object.keys(selected).find((option: string) => selected[option].selected);
    if (!key) {
      return;
    }
    this.patchBranch(priceIndex, branch, {mode: key as TMultiplePriceGpMode});
  }

  protected onBranchValueChange(priceIndex: number, branch: TRuleBranch, event: Event): void {
    const value: number = Number(this.readInputValue(event));
    if (Number.isNaN(value)) {
      return;
    }
    this.patchBranch(priceIndex, branch, {value});
  }

  protected onBranchPriceBandingChange(priceIndex: number, branch: TRuleBranch, priceBanding: boolean): void {
    this.patchBranch(priceIndex, branch, {priceBanding});
  }

  protected onPreviewCostInput(event: Event): void {
    this.previewCost.set(this.readInputValue(event));
  }

  protected onPreviewPriceInput(event: Event): void {
    this.previewPrice.set(this.readInputValue(event));
  }

  protected onPreviewTargetGpInput(event: Event): void {
    this.previewTargetGp.set(this.readInputValue(event));
  }

  protected onRevertForm(): void {
    this.hydrateFromView(this.storeSettings(), this.selectedStoreSignal()?.storeId ?? '');
  }

  protected async onSubmitForm(): Promise<void> {
    if (this.isSubmitDisabled) {
      return;
    }

    let settings: IMultiplePriceChangeRulesStoreSettings = getDeepCopyOfObject(this.draftSettings());

    if (!settings.enabled && settings.rules?.length) {
      const choice: TDisabledRulesChoice = await this.confirmSaveWithRulesDisabled();
      if (choice === 'cancel') {
        return;
      }
      if (choice === 'enable') {
        settings = {...settings, enabled: true};
        this.draftSettings.set({...this.draftSettings(), enabled: true});
      }
    }

    this.pendingSaveSnapshot.set(this.snapshotOf(settings));
    this.formStore.dispatch(settingStoreMultiplePriceChangeRules.setMultiplePriceChangeRules({settings}));
  }

  async onShowModalUnsavedChanges(): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };
    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }

  private previewVatRates(): VatRates {
    const storeVatRates: VatRates = this.vatRates();
    return isNonEmptyObject(storeVatRates) ? storeVatRates : MULTIPLE_PRICE_PREVIEW_FALLBACK_VAT_RATES;
  }

  private previewVatKey(): number {
    return Number(Object.keys(this.previewVatRates())[0] ?? 1);
  }

  private previewVatPercentage(): number {
    return this.previewVatRates()[this.previewVatKey()]?.vatRate ?? 0;
  }

  private findRule(priceIndex: number): IMultiplePriceChangeRule | undefined {
    return this.rules().find((rule: IMultiplePriceChangeRule) => rule.priceIndex === priceIndex);
  }

  /**
   * Serializes only the values the user can edit, so metadata such as `source` and `updatedAt`
   * never make a saved draft look dirty.
   */
  private snapshotOf(settings: IMultiplePriceChangeRulesStoreSettings | undefined): string {
    const safeSettings: IMultiplePriceChangeRulesStoreSettings = settings ?? createDefaultMultiplePriceChangeSettings();
    return JSON.stringify({enabled: !!safeSettings.enabled, rules: safeSettings.rules ?? []});
  }

  private hydrateFromView(view: IMultiplePriceChangeRulesStoreSettings, storeId: string): void {
    const settings: IMultiplePriceChangeRulesStoreSettings = getDeepCopyOfObject(
      view ?? createDefaultMultiplePriceChangeSettings(),
    );
    this.draftStoreId.set(storeId);
    this.draftSettings.set(settings);
    this.lastHydratedSnapshot.set(this.snapshotOf(settings));
    this.expandedPriceIndex.set(settings.rules?.length ? settings.rules[0].priceIndex : null);
    this.hasHydrated.set(true);
  }

  private patchRule(priceIndex: number, patch: Partial<IMultiplePriceChangeRule>): void {
    if (!this.canRulesBeEdited) {
      return;
    }
    const rules: IMultiplePriceChangeRule[] = this.rules().map((rule: IMultiplePriceChangeRule) =>
      rule.priceIndex === priceIndex ? {...rule, ...patch} : rule,
    );
    this.draftSettings.set({...this.draftSettings(), rules});
  }

  private patchBranch(priceIndex: number, branch: TRuleBranch, patch: Partial<IMultiplePriceGpAction>): void {
    const rule: IMultiplePriceChangeRule | undefined = this.findRule(priceIndex);
    if (!rule) {
      return;
    }
    this.patchRule(priceIndex, {[branch]: {...rule[branch], ...patch}});
  }

  private readInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /**
   * Asks what to do when rules exist but the feature switch is off, so rules are never saved
   * as silently inactive.
   */
  private async confirmSaveWithRulesDisabled(): Promise<TDisabledRulesChoice> {
    const ruleCount: number = this.rules().length;
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Enable & Save',
      buttonSecondary: true,
      buttonSecondaryText: 'Save Disabled',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      backdropDismiss: false,
      modalTitle: 'Price Rules Are Not Enabled',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: `You have ${ruleCount} price rule${ruleCount === 1 ? '' : 's'} set up, but Multiple Price Change Rules is switched off.`,
      contextDescription: [
        'While it is off, editing retail price 1 on the stock manager will not change any other prices.',
        '',
        'Enable it now, or save the rules and leave them switched off.',
      ],
      cssClass: ['shared-basic-modal-css'],
    };

    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    if (result.role === 'modal-accept') {
      return 'enable';
    }
    return result.role === 'modal-secondary' ? 'saveDisabled' : 'cancel';
  }

  private async confirmAction(title: string, description: string[], acceptText: string = 'Continue'): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: acceptText,
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: title,
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextDescription: description,
      cssClass: ['shared-basic-modal-css-small'],
    };
    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }
}
